import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import controller from "../../../../Resources/Scripts/_Shared/controller";
import { salesOrderAttachments } from './Endpoints';

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
];

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const ADD_NEW = '__add_new__';

function myAimoParkPermitsAttachmentList(context) {
  const appContainer = context;
  const appConfig = controller.getConfig(context);

  const App = () => {
    const lang = appConfig.localization || {};
    const orderId = appConfig.orderId;
    const allowAdd = appConfig.allowAdd === true;
    const notifyOnChange = appConfig.notifyOnChange === true;

    const [attachments, setAttachments] = useState(appConfig.attachments || []);
    const [pendingAttachmentId, setPendingAttachmentId] = useState(null);
    const [error, setError] = useState('');

    const inputRef = useRef(null);
    const targetAttachmentIdRef = useRef(null);

    const handleReplaceClick = (attachmentId) => {
      setError('');
      targetAttachmentIdRef.current = attachmentId;
      if (inputRef.current) {
        inputRef.current.click();
      }
    };

    const handleFileChange = async (e) => {
      const file = e.target.files && e.target.files[0];
      const targetAttachmentId = targetAttachmentIdRef.current;

      if (!file || !targetAttachmentId) {
        return;
      }

      const isAdding = targetAttachmentId === ADD_NEW;

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        setError(lang.invalidType || 'Please upload a valid PDF, TXT, DOC, DOCX, PNG, JPG, or JPEG file.');
        e.target.value = null;
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(lang.tooLarge || 'File size must be 5MB or less.');
        e.target.value = null;
        return;
      }

      setPendingAttachmentId(targetAttachmentId);
      try {
        const prepareResponse = await salesOrderAttachments.prepareUpload({
          OrderId: orderId,
          FileName: file.name,
          MimeType: file.type,
        });

        if (prepareResponse.status >= 400 || !prepareResponse.body?.UploadUrl) {
          console.error('Prepare upload failed', prepareResponse);
          setError(lang.uploadFailed || 'Upload failed.');
          return;
        }

        const { UploadUrl: uploadUrl, AttachmentId: newAttachmentId } = prepareResponse.body;

        const s3Response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!s3Response.ok) {
          console.error('S3 upload failed', s3Response.status);
          setError(lang.uploadFailed || 'Upload failed.');
          return;
        }

        const updatedAttachments = isAdding
          ? [...attachments, { attachmentId: newAttachmentId, fileName: file.name }]
          : attachments.map((a) =>
              a.attachmentId === targetAttachmentId
                ? { attachmentId: newAttachmentId, fileName: file.name }
                : a,
            );
        const updatedIds = updatedAttachments.map((a) => a.attachmentId);

        const linkResponse = await salesOrderAttachments.link({
          OrderId: orderId,
          AttachmentIds: isAdding ? [newAttachmentId] : updatedIds,
          Append: isAdding,
        });

        if (linkResponse.status >= 400) {
          console.error('Link attachment failed', linkResponse);
          setError(lang.uploadFailed || 'Upload failed.');
          return;
        }

        setAttachments(updatedAttachments);

        if (notifyOnChange) {
          document.dispatchEvent(
            new CustomEvent('aimo:attachments-changed', {
              detail: { orderId, count: updatedAttachments.length },
            }),
          );
        }
      } catch (err) {
        console.error('Replace attachment failed', err);
        setError(lang.uploadFailed || 'Upload failed.');
      } finally {
        setPendingAttachmentId(null);
        targetAttachmentIdRef.current = null;
        if (e.target) {
          e.target.value = null;
        }
      }
    };

    return (
      <>
        <div className="left">{lang.attachment || 'Attachment'}</div>
        <div className="right">
          {attachments.map((a) => {
            const isPending = pendingAttachmentId === a.attachmentId;
            return (
              <div
                key={a.attachmentId}
                className="myaimoparkoverlay__overlay__list__attachment-row"
              >
                <a
                  href={salesOrderAttachments.getDownloadUrl(orderId, a.attachmentId)}
                  className="myaimoparkoverlay__overlay__list__link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {a.fileName}
                </a>
                <a
                  href="#"
                  className="myaimoparkoverlay__overlay__list__link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (pendingAttachmentId === null) handleReplaceClick(a.attachmentId);
                  }}
                  aria-busy={isPending}
                >
                  {isPending ? (lang.replacing || 'Replacing…') : (lang.replace || 'Replace')}
                </a>
              </div>
            );
          })}
          {allowAdd && (
            <div className="myaimoparkoverlay__overlay__list__attachment-row">
              <a
                href="#"
                className="myaimoparkoverlay__overlay__list__link"
                onClick={(e) => {
                  e.preventDefault();
                  if (pendingAttachmentId === null) handleReplaceClick(ADD_NEW);
                }}
                aria-busy={pendingAttachmentId === ADD_NEW}
              >
                {pendingAttachmentId === ADD_NEW
                  ? (lang.adding || 'Uploading…')
                  : (lang.add || 'Add document')}
              </a>
            </div>
          )}
          {lang.requiredNote && attachments.length === 0 && (
            <div className="myaimoparkoverlay__overlay__list__attachment-note">
              {lang.requiredNote}
            </div>
          )}
          {error !== '' && (
            <div className="myaimoparkoverlay__overlay__bottom__inner__error">
              <p dangerouslySetInnerHTML={{ __html: error }} />
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.txt,.doc,.docx,.png,.jpeg,.jpg"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </>
    );
  };

  function init() {
    ReactDOM.render(<App />, appContainer);
  }

  return {
    init,
  };
}

controller.add('myaimopark-permits-attachment-list', myAimoParkPermitsAttachmentList);
